import yasml from '@thirtytech/yasml';
import { useAtom, useAtomValue } from 'jotai';
import { selectedEventAtom, showEventPerformanceAtom } from '../../data/atoms';
import {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  getGetApiInventoryEventsEventIdListingsQueryKey,
  getGetApiInventoryEventsEventIdMappingsQueryKey,
  getGetApiInventoryEventsEventIdSnapshotsNearTermQueryKey,
  getGetApiInventoryEventsEventIdSnapshotsQueryKey,
  getGetApiSalesQueryKey,
  useGetApiInventoryEventsEventIdListings,
  useGetApiInventoryEventsEventIdMappings,
  useGetApiInventoryEventsEventIdSnapshots,
  useGetApiInventoryEventsEventIdSnapshotsNearTerm,
  useGetApiMarketplacesMarketplaceIdEventsEventIdSnapshots,
  useGetApiSales,
} from '../../api';
import dayjs from 'dayjs';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import { getStandardMarketplaceName } from '../../utils/marketplace-utils';
import { useCallback, useState } from 'react';
import { useFlag } from '@unleash/proxy-client-react';

function EventPerformanceState() {
  const [showEmptyStates, showEmptyStatesHandlers] = useDisclosure(false);
  const [showLoadingStates, showLoadingStatesHandlers] = useDisclosure(false);
  const [showErrorStates, showErrorStatesHandlers] = useDisclosure(false);
  const [performanceSnapshotPeriod, setPerformanceSnapshotPeriod] = useLocalStorage({
    key: 'setting-snapshotPeriodPerformance',
    defaultValue: '3m',
    getInitialValueInEffect: false,
  });

  const hasTrendProjectionsFlag = useFlag('performance-trend-projections');

  const [showEventPerformance, setShowEventPerformance] = useAtom(showEventPerformanceAtom);

  const selectedEvent = useAtomValue(selectedEventAtom);

  const {
    data: listingsSold,
    isLoading: isListingsSoldLoading,
    refetch: refetchListingsSold,
  } = useGetApiSales(
    {
      eventId: selectedEvent?.eventId ?? '',
      fromDate: dayjs().subtract(2, 'year').startOf('day').toDate(),
      toDate: dayjs().endOf('day').endOf('day').toDate(),
    },
    {
      query: {
        enabled: !!selectedEvent?.eventId && showEventPerformance,
        select(data) {
          // HACK. The server is not returning all the sale data. So we are just spreading sale over the missing sale data
          // Need to figure out why the server is not returning all the sale data. Likely perf optimization of redundant data.
          // Sale will be missing listingsSold property.
          return data.data.sales.flatMap((sale) => sale.listings.map((listing) => ({ ...listing, event: selectedEvent, sale: listing.sale ?? { ...sale } })));
        },
        queryKey: [
          ...getGetApiSalesQueryKey({
            eventId: selectedEvent?.eventId ?? '',
            fromDate: dayjs().subtract(2, 'year').startOf('day').toDate(),
            toDate: dayjs().endOf('day').endOf('day').toDate(),
          }),
          selectedEvent?.tenantId,
        ],
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  const {
    data: listingsOpen,
    isLoading: isListingsOpenLoading,
    refetch: refetchListingsOpen,
  } = useGetApiInventoryEventsEventIdListings(selectedEvent?.eventId ?? '', {
    query: {
      enabled: !!selectedEvent?.eventId && showEventPerformance,
      select(data) {
        return data.data;
      },
      queryKey: [...getGetApiInventoryEventsEventIdListingsQueryKey(selectedEvent?.eventId ?? ''), selectedEvent?.tenantId],
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const {
    data: inventorySnapshots,
    isLoading: isInventorySnapshotsLoading,
    refetch: refetchInventorySnapshots,
  } = useGetApiInventoryEventsEventIdSnapshots(selectedEvent?.eventId ?? '', {
    query: {
      enabled: !!selectedEvent?.eventId && showEventPerformance && performanceSnapshotPeriod !== '1d',
      select(data) {
        return data.data;
      },
      queryKey: [...getGetApiInventoryEventsEventIdSnapshotsQueryKey(selectedEvent?.eventId ?? ''), selectedEvent?.tenantId],
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const { data: nearTermInventorySnapshots, refetch: refetchNearTermInventorySnapshots } = useGetApiInventoryEventsEventIdSnapshotsNearTerm(selectedEvent?.eventId ?? '', {
    query: {
      enabled: !!selectedEvent?.eventId && showEventPerformance && performanceSnapshotPeriod === '1d',
      select(data) {
        return data.data;
      },
      queryKey: [...getGetApiInventoryEventsEventIdSnapshotsNearTermQueryKey(selectedEvent?.eventId ?? ''), selectedEvent?.tenantId],
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const { data: eventMappings } = useGetApiInventoryEventsEventIdMappings(selectedEvent?.eventId ?? '', {
    query: {
      enabled: !!selectedEvent?.eventId && showEventPerformance,
      select(data) {
        return data.data;
      },
      queryKey: [...getGetApiInventoryEventsEventIdMappingsQueryKey(selectedEvent?.eventId ?? ''), selectedEvent?.tenantId],
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const seatGeekEventId = eventMappings?.find((mapping) => mapping.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek)?.marketplaceEventId;

  const {
    data: seatGeekSnapshots,
    isLoading: isSeatGeekSnapshotsLoading,
    refetch: refetchSeatGeekSnapshots,
  } = useGetApiMarketplacesMarketplaceIdEventsEventIdSnapshots(BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek, seatGeekEventId ?? '', {
    query: {
      enabled: !!seatGeekEventId && showEventPerformance,
      select(data) {
        return data.data;
      },
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetchListingsSold();
    await refetchListingsOpen();
    await refetchInventorySnapshots();
    await refetchNearTermInventorySnapshots();
    await refetchSeatGeekSnapshots();
    setIsRefreshing(false);
  }, [refetchListingsSold, refetchListingsOpen, refetchInventorySnapshots, refetchNearTermInventorySnapshots, refetchSeatGeekSnapshots, setIsRefreshing]);

  const marketplaceBreakdown = listingsSold?.reduce(
    (acc, listing) => {
      const marketplaceName = getStandardMarketplaceName(listing.sale?.marketplaceName ?? 'Other');
      const marketplaceTotal = acc[marketplaceName] ?? 0;
      acc[marketplaceName] = marketplaceTotal + listing.quantity;
      return acc;
    },
    {} as Record<string, number>,
  );

  const soldCost =
    listingsSold?.reduce((acc, listing) => {
      const cost = (listing.unitCost ?? 0) * listing.quantity;
      return acc + cost;
    }, 0) ?? 0;

  const openCost =
    listingsOpen?.reduce((acc, listing) => {
      const cost = (listing.unitCost ?? 0) * listing.quantityRemaining;
      return acc + cost;
    }, 0) ?? 0;

  const totalCost = soldCost && openCost ? soldCost + openCost : 0;

  const soldRevenue =
    listingsSold?.reduce((acc, listing) => {
      const revenue = (listing.unitPrice ?? 0) * listing.quantity;
      return acc + revenue;
    }, 0) ?? 0;

  const soldProfit = soldRevenue - soldCost;

  const soldReturnOnInvestment = (soldProfit / soldCost) * 100;

  const openTickets = listingsOpen?.reduce((acc, listing) => acc + listing.quantityRemaining, 0) ?? 0;

  const openTicketsBroadcasting = listingsOpen?.reduce((acc, listing) => acc + (listing.isBroadcasting ? listing.quantityRemaining : 0), 0) ?? 0;

  const soldTickets = listingsSold?.reduce((acc, listing) => acc + listing.quantity, 0) ?? 0;

  // Hoist the loop-invariant cutoff timestamps out of the reduce callbacks. Previously each
  // iteration recomputed dayjs().subtract(...), which both allocated a dayjs per element and
  // re-read "now". A single shared cutoff is the intended semantics (one fixed window boundary).
  const last24HoursCutoff = dayjs().subtract(24, 'hours');
  const last7DaysCutoff = dayjs().subtract(7, 'days');
  const last30DaysCutoff = dayjs().subtract(30, 'days');

  const soldTicketsLast24Hours =
    listingsSold?.reduce((acc, listing) => {
      const saleDate = dayjs(listing.sale?.createdAt);
      const isWithinLast24Hours = saleDate.isAfter(last24HoursCutoff);
      return acc + (isWithinLast24Hours ? listing.quantity : 0);
    }, 0) ?? 0;

  const soldTicketsLast7Days =
    listingsSold?.reduce((acc, listing) => {
      const saleDate = dayjs(listing.sale?.createdAt);
      const isWithinLast7Days = saleDate.isAfter(last7DaysCutoff);
      return acc + (isWithinLast7Days ? listing.quantity : 0);
    }, 0) ?? 0;

  const soldTicketsLast30Days =
    listingsSold?.reduce((acc, listing) => {
      const saleDate = dayjs(listing.sale?.createdAt);
      const isWithinLast30Days = saleDate.isAfter(last30DaysCutoff);
      return acc + (isWithinLast30Days ? listing.quantity : 0);
    }, 0) ?? 0;

  const unrealizedRevenue =
    listingsOpen?.reduce((acc, listing) => {
      const revenue = (listing.unitPrice ?? 0) * listing.quantityRemaining;
      return acc + revenue;
    }, 0) ?? 0;

  const unrealizedProfit = unrealizedRevenue - openCost;

  const projectedProfit = soldProfit + unrealizedProfit;

  const projectedReturnOnInvestment = (projectedProfit / totalCost) * 100;

  const relevantSnapshots = performanceSnapshotPeriod === '1d' ? nearTermInventorySnapshots : inventorySnapshots;

  // Precompute each sold listing's parsed sale date once. The per-snapshot loop below filters
  // listingsSold by `dayjs(listing.sale?.createdAt).isBefore(date)` for every snapshot, which
  // re-parsed the same createdAt strings O(snapshots) times. Parsing once here keeps identical
  // semantics (same dayjs value, same ms-precision .isBefore comparison) with a single parse pass.
  const soldListingsWithDate = listingsSold?.map((listing) => ({ listing, saleDate: dayjs(listing.sale?.createdAt) }));

  const performanceTrends = relevantSnapshots
    ?.filter(
      (snapshot) =>
        (performanceSnapshotPeriod === '1d' && dayjs(snapshot.createdAt).isAfter(dayjs().subtract(1, 'day'))) ||
        (performanceSnapshotPeriod === '1w' && dayjs(snapshot.createdAt).isAfter(dayjs().subtract(1, 'week'))) ||
        (performanceSnapshotPeriod === '1m' && dayjs(snapshot.createdAt).isAfter(dayjs().subtract(1, 'month'))) ||
        (performanceSnapshotPeriod === '3m' && dayjs(snapshot.createdAt).isAfter(dayjs().subtract(3, 'month'))) ||
        (performanceSnapshotPeriod === '1y' && dayjs(snapshot.createdAt).isAfter(dayjs().subtract(1, 'year'))) ||
        performanceSnapshotPeriod === 'all',
    )
    .sort((a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix())
    .map((item) => {
      const date = dayjs(item.createdAt);
      const dateString = performanceSnapshotPeriod === '1d' ? date.format('h a') : date.format('M/D');
      const dailyOpenTickets = item.openTickets;
      const dailyOpenCost = item.openCost;
      const dailyUnrealizedRevenue = item.openPrice;
      let dailySoldTickets = 0;
      let dailySoldCost = 0;
      let dailySoldRevenue = 0;

      if (soldListingsWithDate) {
        // Single pass over the (pre-parsed) sold listings instead of filter()+forEach(), which
        // allocated an intermediate array each snapshot. Same predicate (saleDate.isBefore(date))
        // and same accumulation order, so results are identical.
        for (const { listing, saleDate } of soldListingsWithDate) {
          if (saleDate.isBefore(date)) {
            dailySoldTickets += listing.quantity;
            dailySoldCost += (listing.unitCost ?? 0) * listing.quantity;
            dailySoldRevenue += (listing.unitPrice ?? 0) * listing.quantity;
          }
        }
      }

      const totalTickets = dailyOpenTickets + dailySoldTickets;
      const currentRoi = soldRevenue && soldCost ? ((dailySoldRevenue - dailySoldCost) / dailySoldCost) * 100 : 0;
      const unrealizedRoi = dailyUnrealizedRevenue && dailyOpenCost ? ((dailyUnrealizedRevenue - dailyOpenCost) / dailyOpenCost) * 100 : 0;
      let potentialRoi = ((dailySoldRevenue + dailyUnrealizedRevenue - (dailySoldCost + dailyOpenCost)) / (dailySoldCost + dailyOpenCost)) * 100;

      if (isNaN(potentialRoi)) {
        potentialRoi = 0;
      }

      const currentRoiPlot = currentRoi > 300 ? 300 : currentRoi;
      const unrealizedRoiPlot = unrealizedRoi > 300 ? 300 : unrealizedRoi;
      const potentialRoiPlot = potentialRoi > 300 ? 300 : potentialRoi;

      return {
        date: dateString,
        currentRoi: currentRoi as number | undefined,
        unrealizedRoi: unrealizedRoi as number | undefined,
        potentialRoi: potentialRoi as number | undefined,
        openTickets: dailyOpenTickets as number | undefined,
        soldTickets: dailySoldTickets as number | undefined,
        totalTickets: totalTickets as number | undefined,
        currentRoiPlot,
        unrealizedRoiPlot,
        potentialRoiPlot,
        roi4: undefined as number | undefined,
        roi5: undefined as number | undefined,
        roi6: undefined as number | undefined,
        amt4: undefined as number | undefined,
        amt5: undefined as number | undefined,
        amt6: undefined as number | undefined,
      };
    });

  // Determine how far away the event is to use for projections - we need at least 7 days of data for projections, and the event has to be within X days depending on the selected period

  const daysToEvent = selectedEvent ? dayjs(selectedEvent.localDateTime).diff(dayjs(), 'day') : null;
  let hoursToEvent = selectedEvent ? dayjs(selectedEvent.utcDateTime ?? selectedEvent.localDateTime).diff(dayjs(), 'hour') + 1 : null;

  const canShowProjections =
    hasTrendProjectionsFlag &&
    daysToEvent !== null &&
    ((performanceSnapshotPeriod === '1d' && daysToEvent <= 1 && (nearTermInventorySnapshots?.length ?? 0) >= 12) ||
      (performanceSnapshotPeriod === '1w' && daysToEvent <= 7) ||
      (performanceSnapshotPeriod === '1m' && daysToEvent <= 31) ||
      (performanceSnapshotPeriod === '3m' && daysToEvent <= 93) ||
      (performanceSnapshotPeriod === '1y' && daysToEvent <= 356) ||
      performanceSnapshotPeriod === 'all');

  // console.log('performanceTrends', performanceTrends);

  if (canShowProjections && performanceTrends && performanceTrends?.length > 0) {
    const mostRecentPeriod = performanceTrends[performanceTrends.length - 1];
    const earliestPeriod = performanceSnapshotPeriod === '1d' ? performanceTrends[0] : performanceTrends[performanceTrends.length - 8]; // For periods longer than 1 day, use the trend from 7 days ago to project forward

    // console.log('mostRecentPeriod', mostRecentPeriod, 'earliestPeriod', earliestPeriod);

    const salesInLookBack = (mostRecentPeriod?.soldTickets ?? 0) - (earliestPeriod?.soldTickets ?? 0);
    const periodsForAverage = performanceSnapshotPeriod === '1d' ? performanceTrends.length : 7;
    const averageSalesPerPeriodRaw = salesInLookBack / periodsForAverage;

    const currentTotalTickets = mostRecentPeriod?.totalTickets ?? 0;
    const periodsUntilEvent = performanceSnapshotPeriod === '1d' ? Math.max(hoursToEvent ?? 0, 0) : Math.max(daysToEvent ?? 0, 0);

    let runningTotalSold = mostRecentPeriod?.soldTickets ?? 0;
    let runningTotalOpen = mostRecentPeriod?.openTickets ?? 0;

    // Calculate integer and remainder parts
    const wholeSalesPerPeriod = Math.floor(averageSalesPerPeriodRaw);
    const remainder = averageSalesPerPeriodRaw - wholeSalesPerPeriod;
    // Distribute the remainder across the periods

    const remainderDistribution: number[] = Array(periodsUntilEvent).fill(0);
    let accumulated = 0;
    for (let i = 0; i < periodsUntilEvent; i++) {
      accumulated += remainder;
      if (accumulated >= 1) {
        remainderDistribution[i] = 1;
        accumulated -= 1;
      }
    }

    const projectedTrends = Array.from({ length: periodsUntilEvent }).map((_, index) => {
      const date = performanceSnapshotPeriod === '1d' ? dayjs().add(index + 1, 'hour') : dayjs().add(index + 1, 'day');
      const dateString = performanceSnapshotPeriod === '1d' ? date.format('h a') : date.format('M/D');
      // Only use whole numbers for projected sales, distribute remainder as extra sales in some periods
      const projectedSalesThisPeriod = wholeSalesPerPeriod + (remainderDistribution[index] || 0);
      const projectedSold = Math.min(currentTotalTickets ?? 0, runningTotalSold + projectedSalesThisPeriod);
      const projectedOpen = Math.max(0, runningTotalOpen - projectedSalesThisPeriod);

      runningTotalSold = projectedSold;
      runningTotalOpen = projectedOpen;
      return {
        date: dateString,
        currentRoi: undefined as number | undefined,
        unrealizedRoi: undefined as number | undefined,
        potentialRoi: undefined as number | undefined,
        openTickets: undefined as number | undefined,
        soldTickets: undefined as number | undefined,
        totalTickets: undefined as number | undefined,
        currentRoiPlot: 0,
        unrealizedRoiPlot: 0,
        potentialRoiPlot: 0,
        roi4: undefined as number | undefined,
        roi5: undefined as number | undefined,
        roi6: undefined as number | undefined,
        amt4: projectedOpen,
        amt5: projectedSold,
        amt6: currentTotalTickets,
      };
    });

    if (projectedTrends.length > 0) {
      performanceTrends.push(...projectedTrends);
    }

    // console.log('performanceTrends', performanceTrends);

    //console.log('salesPerPeriod', salesInLookBack, 'averageSalesPerPeriod', averageSalesPerPeriodRaw);
  }

  return {
    showEmptyStates,
    showEmptyStatesHandlers,
    showLoadingStates,
    showLoadingStatesHandlers,
    showErrorStates,
    showErrorStatesHandlers,
    selectedEvent,
    showEventPerformance,
    setShowEventPerformance,
    listingsSold,
    isListingsSoldLoading: isListingsSoldLoading || showLoadingStates,
    isListingsOpenLoading: isListingsOpenLoading || showLoadingStates,
    performanceSnapshotPeriod,
    setPerformanceSnapshotPeriod,
    performanceTrends,
    isPerformanceTrendsLoading: isInventorySnapshotsLoading || showLoadingStates,
    seatGeekSnapshots,
    isSeatGeekSnapshotsLoading: (isSeatGeekSnapshotsLoading && !!seatGeekEventId) || showLoadingStates,
    marketplaceBreakdown,
    soldCost,
    openCost,
    totalCost,
    soldRevenue,
    soldProfit,
    soldReturnOnInvestment,
    openTickets,
    openTicketsBroadcasting,
    soldTickets,
    soldTicketsLast24Hours,
    soldTicketsLast7Days,
    soldTicketsLast30Days,
    unrealizedRevenue,
    unrealizedProfit,
    projectedProfit,
    projectedReturnOnInvestment,
    refresh,
    isRefreshing,
  };
}

export const { Provider: EventPerformanceProvider, useSelector: useEventPerformance } = yasml(EventPerformanceState);
